import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Download, ArrowLeft, ShieldCheck, FileText, ExternalLink } from "lucide-react";

/**
 * CoaViewer — dedicated viewer page for a product's Certificate(s) of Analysis.
 *
 * URL shape:
 *   /coa/:slug?tier={supply|select}&coa={coaId}
 *
 * Behavior:
 *   · If `?coa=xxx` is present, iframe that specific COA.
 *   · Otherwise iframe the FIRST COA in the product's list.
 *   · If the product has multiple COAs, render a switcher above the iframe so
 *     buyers can flip between lots without leaving the branded page.
 *   · Tier is read from the query so Select-tier products resolve correctly
 *     under the new strict tier-scoped slug lookup (see iter97).
 *
 * Both the catalog card COA badge AND the PDP "Latest Test Results" row point
 * here so the buyer always lands in the branded iframe viewer — no more raw
 * Adobe/Chrome PDF tab bounce.
 */
export default function CoaViewer() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tier = (searchParams.get("tier") || "").toLowerCase();
  const requestedCoa = searchParams.get("coa") || "";
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer the tier-scoped PDP endpoint so Select products resolve. If
        // no tier hint, backend falls back Supply → Select which is fine.
        const url = tier ? `/products/${slug}?tier=${tier}` : `/products/${slug}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        if (!data || !(data.coas || []).length) {
          setNotFound(true);
          return;
        }
        setProduct(data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, tier]);

  const activeCoa = useMemo(() => {
    if (!product) return null;
    const list = product.coas || [];
    if (requestedCoa) {
      return list.find((c) => c.id === requestedCoa) || list[0] || null;
    }
    return list[0] || null;
  }, [product, requestedCoa]);

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-center" data-testid="coa-viewer-notfound">
        <FileText size={40} className="mx-auto text-stone mb-4" />
        <h1 className="font-serif text-3xl mb-2">Certificate not available</h1>
        <p className="text-sm text-stone mb-6">
          We don't have a Certificate of Analysis on file for this product yet.
        </p>
        <Link to="/shop" className="inline-flex items-center gap-2 px-5 py-2.5 border border-charcoal text-charcoal rounded-full text-xs uppercase tracking-[0.22em] hover:bg-charcoal hover:text-bone transition-colors">
          <ArrowLeft size={12} /> Back to catalog
        </Link>
      </div>
    );
  }

  if (!product || !activeCoa) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-center text-stone text-sm" data-testid="coa-viewer-loading">
        Loading certificate…
      </div>
    );
  }

  // iter160 — the branded viewer no longer iframes the PDF (Chrome renders
  // that with a jarring "about:blank" toolbar bar at the top). Instead we
  // show a hero card here and open the raw PDF in a new tab so buyers get
  // Chrome's native full-screen PDF viewer (with thumbnails, download,
  // print, search — all clean and unbranded by our site).
  const pdfSrc = `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/coa/${activeCoa.id}`;
  const downloadHref = pdfSrc;
  const backToPdp = tier ? `/shop/${product.slug}?tier=${tier}` : `/shop/${product.slug}`;
  const coas = product.coas || [];

  const switchLink = (coaId) => {
    const params = new URLSearchParams();
    if (tier) params.set("tier", tier);
    params.set("coa", coaId);
    return `/coa/${product.slug}?${params.toString()}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="coa-viewer-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <Link
            to={backToPdp}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-stone hover:text-charcoal transition-colors mb-3"
            data-testid="coa-viewer-back"
          >
            <ArrowLeft size={12} /> Back to {product.name}
          </Link>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="inline-flex items-center gap-1.5 bg-emerald-700 text-bone text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full">
              <ShieldCheck size={10} strokeWidth={2.5} /> Verified test results
            </span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight leading-none">
            {product.name}
          </h1>
          <p className="text-xs text-stone mt-2">
            Certificate of Analysis · {product.unit_label || "10 vials/kit"}
            {activeCoa.filename && (
              <> · <span className="font-mono">{activeCoa.filename}</span></>
            )}
          </p>
        </div>
        <a
          href={downloadHref}
          download={activeCoa.filename || `COA-${product.sku}.pdf`}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-charcoal text-bone rounded-full text-xs uppercase tracking-[0.22em] hover:bg-oxblood transition-colors"
          data-testid="coa-download-btn"
        >
          <Download size={13} strokeWidth={2.5} /> Download PDF
        </a>
      </div>

      {/* Multi-COA switcher (only when >1). Highlights the active COA and
          lets buyers flip between lots without leaving the page. */}
      {coas.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="coa-viewer-switcher">
          {coas.map((c) => {
            const isActive = c.id === activeCoa.id;
            return (
              <Link
                key={c.id}
                to={switchLink(c.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] uppercase tracking-[0.18em] font-mono transition-colors border ${
                  isActive
                    ? "bg-charcoal text-bone border-charcoal"
                    : "bg-bone text-charcoal border-borderSubtle hover:border-charcoal"
                }`}
                data-testid={`coa-viewer-switcher-${c.id}`}
                title={c.filename}
              >
                <FileText size={11} strokeWidth={2} />
                <span className="truncate max-w-[240px]">{c.filename || "certificate.pdf"}</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* PDF hero — a branded landing card that opens the actual PDF in a
          new tab. The buyer gets Chrome's native viewer (thumbnails,
          download, print) without our iframe wrapping "about:blank" chrome
          around it. */}
      <div
        className="border border-borderSubtle bg-white rounded-2xl overflow-hidden shadow-sm"
        data-testid="coa-viewer-hero"
      >
        <div className="bg-gradient-to-br from-charcoal to-[#1B1614] text-bone px-8 md:px-12 py-10 md:py-14">
          <div className="flex items-start justify-between gap-8 flex-wrap">
            <div className="max-w-xl">
              <p className="overline text-[#DDA94E] mb-3">Certificate of Analysis</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mb-3">
                {product.name}
              </h2>
              <p className="text-[14px] text-bone/80 leading-relaxed">
                Third-party HPLC and mass-spectrometry testing verifying identity, purity, and chromatographic composition — sourced directly from the manufacturer.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <a
                  href={pdfSrc}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#DDA94E] text-[#1B1614] rounded-full text-[12px] uppercase tracking-[0.22em] font-mono hover:bg-bone transition-colors"
                  data-testid="coa-open-btn"
                >
                  <ExternalLink size={13} strokeWidth={2.5} /> Open certificate
                </a>
                <a
                  href={downloadHref}
                  download={activeCoa.filename || `COA-${product.sku}.pdf`}
                  className="inline-flex items-center gap-2 px-6 py-3 border border-bone/40 text-bone rounded-full text-[12px] uppercase tracking-[0.22em] font-mono hover:bg-bone hover:text-charcoal transition-colors"
                  data-testid="coa-download-btn-hero"
                >
                  <Download size={13} strokeWidth={2.5} /> Download
                </a>
              </div>
            </div>
            <div className="flex-shrink-0 hidden md:flex items-center justify-center w-32 h-40 border border-[#DDA94E]/50 bg-black/20 rounded-lg" aria-hidden="true">
              <FileText size={56} strokeWidth={1} className="text-[#DDA94E]" />
            </div>
          </div>
        </div>
        <div className="px-8 md:px-12 py-5 bg-bone border-t border-borderSubtle grid grid-cols-1 sm:grid-cols-3 gap-4 text-[13px]">
          <div>
            <p className="overline text-charcoal/60 mb-1">File</p>
            <p className="font-mono text-charcoal truncate" title={activeCoa.filename}>
              {activeCoa.filename || "certificate.pdf"}
            </p>
          </div>
          <div>
            <p className="overline text-charcoal/60 mb-1">Product</p>
            <p className="font-mono text-charcoal">{product.sku || product.name}</p>
          </div>
          <div>
            <p className="overline text-charcoal/60 mb-1">Purpose</p>
            <p className="text-charcoal">Research use only</p>
          </div>
        </div>
      </div>

      <p className="text-[13px] text-stone mt-4 leading-relaxed max-w-2xl">
        This Certificate of Analysis is provided directly by the manufacturer for verification purposes.
        Bulk Buy Kits products are sold for research use only.
      </p>
    </div>
  );
}
